import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, EmptyState, StatusBadge, Modal, PageHeader, Tabs } from '@/components/ui';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import './recordings/cdr.css';

interface Vm {
  id: number;
  from_number: string;
  from_lookup?: unknown;
  file_url?: string;
  duration: number;
  transcript?: string;
  created_at: string;
  read_at?: string | null;
}

type Filter = 'all' | 'unread';

/** embedded = לשונית "תא קולי" בתוך מסך השיחות (בלי כותרת וריווח משלה). */
export function AdminVoicemail({ embedded = false }: { embedded?: boolean } = {}) {
  const [rows, setRows] = useState<Vm[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [deleteTarget, setDeleteTarget] = useState<Vm | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi
      .voicemails()
      .then((res) => setRows(res.items as Vm[]))
      .catch(() => toast('שגיאה בטעינת התא הקולי', 'danger'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const unreadCount = useMemo(() => rows.filter((r) => !r.read_at).length, [rows]);
  const shown = useMemo(() => (filter === 'unread' ? rows.filter((r) => !r.read_at) : rows), [rows, filter]);

  async function toggleRead(vm: Vm) {
    const wasUnread = !vm.read_at;
    setRows((prev) => prev.map((r) => (r.id === vm.id ? { ...r, read_at: wasUnread ? new Date().toISOString() : null } : r)));
    try {
      if (wasUnread) await adminApi.markVoicemailRead(vm.id);
      else await adminApi.markVoicemailUnread(vm.id);
    } catch {
      toast('שגיאה בעדכון', 'danger');
      load();
    }
  }

  async function remove(vm: Vm) {
    try {
      await adminApi.deleteVoicemail(vm.id);
      toast('ההודעה נמחקה', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== vm.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const pad = embedded ? undefined : 'var(--gap)';
  if (loading) return <div style={{ padding: pad }}><Card>טוען…</Card></div>;

  return (
    <div className="stack" style={{ padding: pad }}>
      {!embedded && (
        <PageHeader
          title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            תא קולי כללי
            {unreadCount > 0 && <StatusBadge tone="danger" size="sm">{unreadCount} חדש</StatusBadge>}
          </span>}
        />
      )}

      <Tabs
        items={[
          { id: 'all', label: 'הכל', count: rows.length },
          { id: 'unread', label: 'לא נקראו', count: unreadCount },
        ]}
        value={filter}
        onChange={(id) => setFilter(id as Filter)}
        size="sm"
      />

      {shown.length === 0 ? (
        <Card><EmptyState icon="voicemail" title={filter === 'unread' ? 'אין הודעות שלא נקראו' : 'אין הודעות בתא הקולי'} /></Card>
      ) : (
        shown.map((vm, i) => {
          const unread = !vm.read_at;
          return (
            <div key={vm.id} className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12, ['--d' as string]: `${0.02 + Math.min(i, 15) * 0.03}s` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <PhoneCell number={vm.from_number} lookup={vm.from_lookup as never} />
                  {unread && <StatusBadge tone="accent" size="sm" dot>חדש</StatusBadge>}
                </div>
                <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{new Date(vm.created_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
              {vm.file_url && <AudioPlayer src={vm.file_url} duration={vm.duration} />}
              {vm.transcript && (
                <p className="cdr-fade" style={{ margin: 0, padding: '10px 14px', borderRadius: 12, background: 'var(--glass-soft)', fontSize: 'var(--t-sm)', lineHeight: 1.6, color: 'var(--text-2)' }}>{vm.transcript}</p>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button type="button" className="btn soft" style={{ height: 32 }} onClick={() => toggleRead(vm)}>{unread ? 'סמן כנקרא' : 'סמן כלא נקרא'}</button>
                {vm.file_url && <a href={vm.file_url} download target="_blank" rel="noreferrer" className="btn soft" style={{ height: 32 }}>הורד</a>}
                <span style={{ flex: 1 }} />
                <button type="button" className="btn ghost" style={{ height: 32, color: 'var(--danger)' }} onClick={() => setDeleteTarget(vm)}>מחק</button>
              </div>
            </div>
          );
        })
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת הודעה"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את ההודעה הקולית מ-<strong dir="ltr">{deleteTarget.from_number}</strong>?</p>
        </Modal>
      )}
    </div>
  );
}
